<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-stately/data';
import {HeaderInfo, TypeContext, ClassAPI, FunctionAPI, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-stately/data/package.json';

---
category: Data
keywords: [lists, insert, remove, state]
---

# useListData

<p>{docs.exports.useListData.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useListData']} />

## Introduction

React requires all data structures passed as props to be immutable. This enables them to be diffed correctly to determine
what has changed since the last render. This can be challenging to accomplish from scratch in a performant way in JavaScript.

`useListData` helps manage an immutable list data structure, with helper methods to update the data in an efficient way.
Since the data is stored in React state, calling these methods to update the data automatically causes the component
to re-render accordingly.

In addition, `useListData` stores selection state for the list, based on unique item keys. This can be updated programmatically,
and is automatically updated when items are removed from the list.

## API

<FunctionAPI function={docs.exports.useListData} links={docs.links} />

## Options

<ClassAPI links={docs.links} class={docs.links[docs.exports.useListData.parameters[0].value.base.id]} />

## Interface

<ClassAPI links={docs.links} class={docs.links[docs.exports.useListData.return.base.id]} />

## Example

To construct a list, pass an initial set of items along with a function to get a key for each item.
You can use the state returned by `useListData` to render a [collection component](collections.html).

This example renders a `ListBox` using the items managed by `useListData`. It uses the `name` property of each item
as the unique key for that item, and the `items` property as the children. In addition, it manages the selection state
for the listbox, which will automatically be updated when items are removed from the tree.

```tsx
let list = useListData({
  initialItems: [
    {name: 'Aardvark'},
    {name: 'Kangaroo'},
    {name: 'Snake'}
  ],
  initialSelectedKeys: ['Kangaroo'],
  getKey: item => item.name
});

<ListBox
  items={list.items}
  selectedKeys={list.selectedKeys}
  onSelectionChange={list.setSelectedKeys}>
  {item => <Item key={item.name}>{item.name}</Item>}
</ListBox>
```

### Inserting items

To insert a new item into the list, use the `insert` method or one of the other convenience methods.
Each of these methods also accepts multiple items, so you can insert multiple items at once.

```tsx
// Insert an item after the first one
list.insert(1, {name: 'Horse'});

// Insert multiple items
list.insert(1, {name: 'Horse'}, {name: 'Giraffe'});
```

```tsx
// Insert an item before another item
list.insertAfter('Kangaroo', {name: 'Horse'});

// Insert multiple items before another item
list.insertAfter('Kangaroo', {name: 'Horse'}, {name: 'Giraffe'});
```

```tsx
// Insert an item after another item
list.insertAfter('Kangaroo', {name: 'Horse'});

// Insert multiple items after another item
list.insertAfter('Kangaroo', {name: 'Horse'}, {name: 'Giraffe'});
```

```tsx
// Append an item
list.append({name: 'Horse'});

// Append multiple items
list.append({name: 'Horse'}, {name: 'Giraffe'});
```

```tsx
// Prepend an item
list.prepend({name: 'Horse'});

// Prepend multiple items
list.prepend({name: 'Horse'}, {name: 'Giraffe'});
```

### Removing items

```tsx
// Remove an item
list.remove('Kangaroo');

// Remove multiple items
list.remove('Kangaroo', 'Snake');
```

```tsx
// Remove all selected items
list.removeSelectedItems();
```

### Moving items

```tsx
list.move('Snake', 0);
```

### Updating items

```tsx
list.update('Snake', {name: 'Rattle Snake'});
```
